import React, { useState, useEffect, useCallback } from "react";
import SideBar from "../components/SideBar";
import TopBar from "../components/TopBar";

function AllResources() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const itemsPerPage = 6;

  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

  const fetchArticles = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      const url = `${TEST_BASE_URL}/articles?category=singles&page=${currentPage}&per_page=${itemsPerPage}`;

      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      let articlesData = [];
      let pagination = {};

      if (data.data?.articles) {
        articlesData = data.data.articles;
        pagination = data.data.pagination || {};
      } else if (data.articles) {
        articlesData = data.articles;
        pagination = data.pagination || {};
      } else if (data.data && Array.isArray(data.data)) {
        articlesData = data.data;
        pagination = data.pagination || {};
      } else if (Array.isArray(data)) {
        articlesData = data;
      }

      const totalCount =
        pagination.total || pagination.total_items || articlesData.length || 0;
      const totalPages =
        pagination.last_page ||
        pagination.total_pages ||
        Math.ceil(totalCount / itemsPerPage);

      setArticles(articlesData);
      setTotalArticles(totalCount);
      setTotalPages(totalPages);
    } catch (err) {
      console.error("Fetch Articles Error:", err);
      setError(`Unable to load articles: ${err.message}`);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, TEST_BASE_URL]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <section className="dashboard">
      <SideBar isOpen={sidebarOpen} />
      <div className="body-content">
        <TopBar onToggleSidebar={toggleSidebar} />
        <div className="bottom-content">
          <div className="d-flex justify-content-between align-items-center my-4">
            <h5>All Resources</h5>
          </div>

          <div className="row">
            {loading ? (
              <div className="col-12">
                <p>Loading articles...</p>
              </div>
            ) : error ? (
              <div className="col-12">
                <p className="error-text">{error}</p>
                <button
                  onClick={() => setCurrentPage(currentPage)}
                  className="btn btn-retry"
                >
                  Retry
                </button>
              </div>
            ) : articles.length > 0 ? (
              articles.map((item) => (
                <div className="col-md-4 mb-4" key={item.id}>
                  <div className="recentcard">
                    {item.thumbnail ? (
                      <img
                        src={
                          item.thumbnail.startsWith("http")
                            ? item.thumbnail
                            : `https://rccgbournemouth.org${
                                item.thumbnail.startsWith("/") ? "" : "/"
                              }${item.thumbnail}`
                        }
                        alt={item.title || "Article Image"}
                        width="100%"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                        }}
                      />
                    ) : null}
                    <h6>{item.title || "Untitled"}</h6>
                    <p>
                      {item.body?.slice(0, 120) ||
                        item.sub_title ||
                        "No description available..."}
                    </p>
                    <div className="recent-buttons">
                      <a href={`/resources-article/${item.id}`}>Read More</a>
                      {/* <a href={`/singlesfellowship`}>Share</a> */}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <p>No articles found.</p>
              </div>
            )}
          </div>

          {!loading && articles.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <small className="text-muted">
                Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong>{" "}
                –{" "}
                <strong>
                  {Math.min(currentPage * itemsPerPage, totalArticles)}
                </strong>{" "}
                of <strong>{totalArticles}</strong> articles
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    const isVisible =
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1);

                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <li key={`ellipsis-${page}`} className="page-item">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }

                    if (!isVisible) return null;

                    return (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default AllResources;
